import { useState } from 'react';

import { Flex, Text } from '@tradener/lumen';

import useColorMode from '~/hooks/useColorMode';

import NineBoxActiveFilters from './NineBoxActiveFilters';
import NineBoxHeader from './NineBoxHeader';
import NineBoxMetricRail from './NineBoxMetricRail';
import { NineBoxRow, NineBoxSkeletonRow } from './NineBoxTableRows';
import {
  CELL_HEIGHT,
  HEADER_HEIGHT,
  OUTER_CARD_RADIUS,
  TABLE_RADIUS,
  buildGridTemplateColumns,
  buildTableMinWidth,
  createEmptyMetricData,
  resolveTableDividerColor,
  resolveTableHeaderBackground,
  resolveTableStructureTextColor,
  resolveTableSurfaceBackground,
} from './constants';
import {
  NINEBOX_RATINGS,
  NINEBOX_YEARS,
  NineBoxMetricDataProps,
  NineBoxMetricProps,
  NineBoxRatingProps,
  NineBoxYearProps,
} from './mockData';
import { NineBoxCellSelectionProps } from './types';

export interface NineBoxProps {
  metric: NineBoxMetricProps;
  selectedCell?: NineBoxCellSelectionProps;
  selectedRating?: NineBoxRatingProps;
  years?: NineBoxYearProps[];
  metricData?: NineBoxMetricDataProps;
  isLoading: boolean;
  hasData: boolean;
  consideredPeriodLabel?: string;
  onMetricChange: (metric: NineBoxMetricProps) => void;
  onClearFilters?: () => void;
  onRatingSelect: (rating: NineBoxRatingProps) => void;
  onCellSelect: (selection: NineBoxCellSelectionProps) => void;
}

const COLLAPSIBLE_SECTION_ID = 'ninebox-collapsible-section';

function NineBox({
  metric,
  selectedCell,
  selectedRating,
  years: yearsProp,
  metricData: metricDataProp,
  isLoading,
  hasData,
  consideredPeriodLabel,
  onMetricChange,
  onClearFilters,
  onRatingSelect,
  onCellSelect,
}: NineBoxProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { mode } = useColorMode();

  const years = yearsProp?.length ? yearsProp : NINEBOX_YEARS;
  const metricData = metricDataProp || createEmptyMetricData(years);
  const lastYearIndex = years.length - 1;
  const bodyRatings = NINEBOX_RATINGS.filter((rating) => rating !== 'TOTAL');
  const lastBodyRating = bodyRatings[bodyRatings.length - 1];

  const tableGridTemplateColumns = buildGridTemplateColumns(years.length);
  const tableMinWidth = buildTableMinWidth(years.length);
  const bodyRowsMaxHeight = `${bodyRatings.length * Number.parseFloat(CELL_HEIGHT)}rem`;
  const tableSurfaceBg = resolveTableSurfaceBackground(mode);
  const tableHeaderBg = resolveTableHeaderBackground(mode);
  const tableDividerColor = resolveTableDividerColor(mode);
  const tableStructureTextColor = resolveTableStructureTextColor(mode);

  const hasSelectedCell = Boolean(selectedCell);
  const hasRatingOnlyFilter = Boolean(selectedRating) && !hasSelectedCell;
  const hasActiveFilter = hasSelectedCell || hasRatingOnlyFilter;

  const handleToggleCollapse = () => {
    setIsCollapsed((previous) => {
      const nextCollapsed = !previous;

      if (nextCollapsed) {
        onClearFilters?.();
      }

      return nextCollapsed;
    });
  };

  return (
    <Flex direction="column" width="full" data-testid="ninebox">
      <Flex direction="column" width="full" borderRadius={OUTER_CARD_RADIUS} bg="transparent" rowGap="5">
        <NineBoxHeader
          isCollapsed={isCollapsed}
          collapsibleSectionId={COLLAPSIBLE_SECTION_ID}
          onToggleCollapse={handleToggleCollapse}
        />

        <Flex
          id={COLLAPSIBLE_SECTION_ID}
          data-testid={COLLAPSIBLE_SECTION_ID}
          direction="column"
          display={isCollapsed ? 'none' : 'flex'}
          rowGap="5"
          aria-hidden={isCollapsed}
        >
          <Flex align="center" justify="center" width="full" gap="4" data-testid="ninebox-matrix-layout">
            <NineBoxMetricRail metric={metric} onMetricChange={onMetricChange} />

            <Flex direction="column" width="66.6667%" minW="0" rowGap="3">
              <Flex
                direction="column"
                width="full"
                borderRadius={TABLE_RADIUS}
                bg={tableSurfaceBg}
                overflow="hidden"
                data-testid="ninebox-table-container"
              >
                <Flex width="full" overflowX="auto">
                  <Flex direction="column" width="full" minW={tableMinWidth} overflow="hidden">
                    <Flex display="grid" gridTemplateColumns={tableGridTemplateColumns} minH={HEADER_HEIGHT} bg={tableHeaderBg}>
                      <Flex px="3" align="center" whiteSpace="nowrap">
                        <Text textStyle="body1-semibold" color={tableStructureTextColor} lineHeight="1">
                          Rating
                        </Text>
                      </Flex>

                      {years.map((year) => (
                        <Flex key={`header-${year}`} px="3" align="center" justify="center">
                          <Text textStyle="body1-semibold" color={tableStructureTextColor} lineHeight="1">
                            {year}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>

                    <Flex
                      width="full"
                      h="0"
                      borderBottom="1px solid"
                      borderColor={tableDividerColor}
                      data-testid="ninebox-header-divider"
                    />

                    <Flex direction="column" overflow="hidden" maxH={bodyRowsMaxHeight}>
                      {isLoading
                        ? bodyRatings.map((rating) => (
                            <NineBoxSkeletonRow
                              key={`skeleton-row-${rating}`}
                              rating={rating}
                              years={years}
                              tableGridTemplateColumns={tableGridTemplateColumns}
                              lastYearIndex={lastYearIndex}
                              lastBodyRating={lastBodyRating}
                            />
                          ))
                        : bodyRatings.map((rating) => (
                            <NineBoxRow
                              key={`row-${rating}`}
                              rating={rating}
                              years={years}
                              metricData={metricData}
                              selectedCell={selectedCell}
                              selectedRating={selectedRating}
                              tableGridTemplateColumns={tableGridTemplateColumns}
                              lastYearIndex={lastYearIndex}
                              lastBodyRating={lastBodyRating}
                              hasSelectedCell={hasSelectedCell}
                              hasRatingOnlyFilter={hasRatingOnlyFilter}
                              hasData={hasData}
                              isLoading={isLoading}
                              onRatingSelect={onRatingSelect}
                              onCellSelect={onCellSelect}
                            />
                          ))}
                    </Flex>

                    <Flex
                      width="full"
                      h="0"
                      borderBottom="1px solid"
                      borderColor={tableDividerColor}
                      data-testid="ninebox-footer-divider"
                    />

                    {isLoading ? (
                      <NineBoxSkeletonRow
                        rating="TOTAL"
                        years={years}
                        tableGridTemplateColumns={tableGridTemplateColumns}
                        lastYearIndex={lastYearIndex}
                        lastBodyRating={lastBodyRating}
                      />
                    ) : (
                      <NineBoxRow
                        rating="TOTAL"
                        years={years}
                        metricData={metricData}
                        selectedCell={selectedCell}
                        selectedRating={selectedRating}
                        tableGridTemplateColumns={tableGridTemplateColumns}
                        lastYearIndex={lastYearIndex}
                        lastBodyRating={lastBodyRating}
                        hasSelectedCell={hasSelectedCell}
                        hasRatingOnlyFilter={hasRatingOnlyFilter}
                        hasData={hasData}
                        isLoading={isLoading}
                        onRatingSelect={onRatingSelect}
                        onCellSelect={onCellSelect}
                      />
                    )}
                  </Flex>
                </Flex>
              </Flex>

              <NineBoxActiveFilters
                consideredPeriodLabel={consideredPeriodLabel}
                hasActiveFilter={hasActiveFilter}
                selectedRating={selectedRating}
                selectedCell={selectedCell}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default NineBox;
